import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { getFirestore, collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { auth } from '../firebase';
import ReactMarkdown from 'react-markdown';

const db = getFirestore();
const functions = getFunctions();

// Connect to Functions emulator in development
if (process.env.NODE_ENV === 'development') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch (error) {
    // Emulator already connected, ignore
    console.log('Functions emulator connection:', error.message);
  }
}

const PropertyDescriptionV2 = ({ setCurrentPage }) => {
  const [nombrePromocion, setNombrePromocion] = useState('');
  const [tipoPropiedad, setTipoPropiedad] = useState('Apartamentos');
  const [tipoPromocion, setTipoPromocion] = useState('Exclusiva');
  const [ubicacion, setUbicacion] = useState('');
  const [listadoPropiedades, setListadoPropiedades] = useState('');
  
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [storedGenerations, setStoredGenerations] = useState([]);
  const [expandedGeneration, setExpandedGeneration] = useState(null);

  useEffect(() => {
    fetchStoredGenerations();
  }, []);

  const fetchStoredGenerations = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const q = query(
        collection(db, "PropertyDescriptionsV2"),
        where("userId", "==", user.uid),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(q);
      const generations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStoredGenerations(generations);
    } catch (error) {
      console.error("Error fetching stored generations:", error);
      setError(`Failed to fetch stored generations: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setGeneratedContent(null);

    try {
      const generateDescription = httpsCallable(functions, 'generatePropertyDescription');
      const response = await generateDescription({ 
        nombrePromocion, 
        tipoPropiedad,
        tipoPromocion,
        ubicacion, 
        listadoPropiedades 
      });

      const responseData = response.data.result;
      setGeneratedContent(responseData);

      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('User not authenticated');
        }

        const docRef = await addDoc(collection(db, "PropertyDescriptionsV2"), {
          userId: user.uid,
          nombrePromocion,
          tipoPropiedad,
          tipoPromocion,
          ubicacion,
          listadoPropiedades,
          generatedContent: responseData,
          timestamp: new Date()
        });
        console.log("Document written with ID: ", docRef.id);
        fetchStoredGenerations();
      } catch (firestoreError) {
        console.error("Error adding document to Firestore: ", firestoreError);
        setError(`Failed to store generation in database: ${firestoreError.message}`);
      }

    } catch (e) {
      console.error("There was a problem with the generation:", e);
      setError(`An error occurred: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGeneration = (id) => {
    setExpandedGeneration(expandedGeneration === id ? null : id);
  };

  const propertyTypes = ["Apartamentos", "Villas", "Casas", "Áticos", "Dúplex"];

  return (
    <div className="max-w-4xl mx-auto p-4">
      <button onClick={() => setCurrentPage('home')} className="mb-4 flex items-center text-[rgb(100,204,201)] hover:underline">
        <ArrowLeft className="mr-2" /> Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold mb-4">Property Description Generator v2</h2>
      <form onSubmit={handleSubmit} className="mb-6">
        
        <div className="mb-4">
          <label htmlFor="nombrePromocion" className="block text-sm font-medium text-gray-700">Nombre de la Promoción</label>
          <input type="text" id="nombrePromocion" value={nombrePromocion} onChange={(e) => setNombrePromocion(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50" required />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="tipoPropiedad" className="block text-sm font-medium text-gray-700">Tipo de Propiedad</label>
            <select id="tipoPropiedad" value={tipoPropiedad} onChange={(e) => setTipoPropiedad(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50" required>
              {propertyTypes.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="tipoPromocion" className="block text-sm font-medium text-gray-700">Tipo de Promoción</label>
            <select id="tipoPromocion" value={tipoPromocion} onChange={(e) => setTipoPromocion(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50" required>
              <option value="Exclusiva">Exclusiva</option>
              <option value="Abierto">Abierto</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="ubicacion" className="block text-sm font-medium text-gray-700">Ubicación</label>
          <input type="text" id="ubicacion" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50" placeholder="Ciudad, Provincia/Región" required />
        </div>

        <div className="mb-4">
          <label htmlFor="listadoPropiedades" className="block text-sm font-medium text-gray-700">Descripción</label>
          <textarea id="listadoPropiedades" value={listadoPropiedades} onChange={(e) => setListadoPropiedades(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50"
            rows="5" placeholder="Descripción libre con todas las características, servicios, zonas comunes, ubicación, conexiones, etc." required />
        </div>
        
        <button type="submit" className="bg-[rgb(100,204,201)] text-white py-2 px-4 rounded hover:bg-[rgb(90,184,181)] transition-colors" disabled={isLoading}>
          {isLoading ? 'Generating...' : 'Generate Description'}
        </button>
      </form>

      {isLoading && (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(100,204,201)]"></div>
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}

      {generatedContent && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-semibold mb-2">Generated Content for {nombrePromocion}</h3>
          <div className="mb-4">
            <h4 className="text-lg font-semibold">SEO Titles:</h4>
            <ul className="list-disc pl-5">
              {generatedContent.titles.map((title, index) => <li key={index}>{title}</li>)}
            </ul>
          </div>
          {generatedContent.meta_description && (
            <div className="mb-4">
              <h4 className="text-lg font-semibold">SEO Meta Description:</h4>
              <p className="text-gray-700 bg-gray-50 p-3 rounded">{generatedContent.meta_description}</p>
            </div>
          )}
          <div className="mb-4">
            <h4 className="text-lg font-semibold">Description:</h4>
            <ReactMarkdown>{generatedContent.description}</ReactMarkdown>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-xl font-semibold mb-4">Stored Generations</h3>
        {storedGenerations.map((gen) => (
          <div key={gen.id} className="mb-4 border rounded-lg overflow-hidden">
            <div
              className="flex justify-between items-center p-4 bg-gray-100 cursor-pointer"
              onClick={() => toggleGeneration(gen.id)}
            >
              <h4 className="font-medium">{gen.nombrePromocion}</h4>
              {expandedGeneration === gen.id ? <ChevronUp /> : <ChevronDown />}
            </div>
            {expandedGeneration === gen.id && (
              <div className="p-4">
                <div className="mb-4">
                  <h5 className="font-medium">SEO Titles:</h5>
                  <ul className="list-disc pl-5">
                    {gen.generatedContent.titles.map((title, index) => <li key={index}>{title}</li>)}
                  </ul>
                </div>
                {gen.generatedContent.meta_description && (
                  <div className="mb-4">
                    <h5 className="font-medium">SEO Meta Description:</h5>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{gen.generatedContent.meta_description}</p>
                  </div>
                )}
                <div className="mb-4">
                  <h5 className="font-medium">Description:</h5>
                  <ReactMarkdown>{gen.generatedContent.description}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PropertyDescriptionV2; 